import { useState } from 'react';
import { Calendar, Target, TrendingUp, Clock, Edit2, Check, X, Loader2 } from 'lucide-react';

interface MotivationStatsHeaderProps {
  userAspiration?: string;
  totalSessions?: number;
  currentStreak?: number;
  lastSessionDate?: string;
  minutesCoached?: number;
  onAspirationUpdate?: (newGoal: string) => Promise<void>;
  isUpdatingAspiration?: boolean;
  hideStats?: boolean;
}

const MotivationStatsHeader = ({
  userAspiration = "To become a senior leader in tech",
  totalSessions = 12,
  currentStreak = 7,
  lastSessionDate = "2 days ago",
  minutesCoached = 240,
  onAspirationUpdate,
  isUpdatingAspiration = false,
  hideStats = false
}: MotivationStatsHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');

  const handleEditStart = () => {
    setEditValue(userAspiration);
    setIsEditing(true);
    setError('');
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setError('');
  };

  const handleEditSave = async () => {
    if (!editValue.trim() || editValue.trim().length < 10) {
      setError('Goal must be at least 10 characters long');
      return;
    }

    if (!onAspirationUpdate) {
      setError('Update functionality not available');
      return;
    }

    try {
      await onAspirationUpdate(editValue.trim());
      setIsEditing(false);
      setEditValue('');
      setError('');
    } catch (err) {
      setError('Failed to update goal. Please try again.');
    }
  };

  const formatStreak = (streak: number) => {
    if (streak === 0) return "Start today";
    if (streak === 1) return "1 day";
    return `${streak} days`;
  };

  // If hideStats is true, render only the aspirational content
  if (hideStats) {
    return (
      <div className="w-full">
        {isEditing ? (
          <div className="space-y-3 max-w-2xl mx-auto">
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full p-4 text-lg font-display font-bold text-slate-900 bg-white border border-slate-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
              rows={3}
              placeholder="Enter your coaching goal..."
              disabled={isUpdatingAspiration}
            />
            {error && (
              <p className="text-sm text-red-600 font-medium text-center">{error}</p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleEditSave}
                disabled={isUpdatingAspiration}
                className="flex items-center gap-2 px-4 py-2 bg-royal-blue text-white text-sm font-medium rounded-xl hover:bg-royal-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdatingAspiration ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </button>
              <button
                onClick={handleEditCancel}
                disabled={isUpdatingAspiration}
                className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="group text-center">
            <p className="font-display text-lg font-medium text-slate-600 leading-relaxed">
              {userAspiration}
            </p>
            {onAspirationUpdate && (
              <button
                onClick={handleEditStart}
                className="mt-3 opacity-0 group-hover:opacity-100 inline-flex items-center gap-2 px-3 py-1.5 text-slate-500 hover:text-royal-blue hover:bg-white/50 rounded-lg transition-all duration-200"
                title="Edit your goal"
              >
                <Edit2 className="h-4 w-4" />
                <span className="text-sm">Edit</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-5 gap-6">
        {/* North Star Aspiration Card - 60% width (3 columns) */}
        <div className="col-span-3">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-coral/10 via-coral/5 to-transparent border border-coral/20 p-8 backdrop-blur-sm">
            {/* Glassmorphism overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-transparent" />
            
            <div className="relative z-10 flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm">
                <Target className="h-6 w-6 text-royal-blue" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-display font-medium text-royal-blue uppercase tracking-wider mb-1">
                  First you aspire, then you soar...
                </p>
                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full p-3 text-lg font-display font-bold text-slate-900 bg-white/90 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                      rows={3}
                      placeholder="Enter your coaching goal..."
                      disabled={isUpdatingAspiration}
                    />
                    {error && (
                      <p className="text-sm text-red-600 font-medium">{error}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleEditSave}
                        disabled={isUpdatingAspiration}
                        className="flex items-center gap-2 px-3 py-1.5 bg-royal-blue text-white text-sm font-medium rounded-lg hover:bg-royal-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isUpdatingAspiration ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={handleEditCancel}
                        disabled={isUpdatingAspiration}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <h2 className="font-display text-xl font-bold text-slate-900 tracking-tight leading-tight flex-1">
                      {userAspiration}
                    </h2>
                    {onAspirationUpdate && (
                      <button
                        onClick={handleEditStart}
                        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-500 hover:text-royal-blue hover:bg-white/50 rounded-lg transition-all duration-200"
                        title="Edit your goal"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Subtle background pattern */}
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-coral/10 to-transparent rounded-full blur-2xl" />
          </div>
        </div>

        {/* Stats Widgets - 20% width each (1 column each) */}
        <div className="col-span-1">
          <div className="h-full rounded-2xl bg-white border border-alto-grey p-6 shadow-sm hover:shadow-md transition-all duration-200">
            <div className="flex flex-col h-full justify-between">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                  <TrendingUp className="h-5 w-5 text-royal-blue" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <div className="text-2xl font-display font-bold text-slate-900 mb-1">
                  {totalSessions}
                </div>
                <p className="text-sm font-body text-slate-600">
                  Sessions completed
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1">
          <div className="h-full rounded-2xl bg-white border border-alto-grey p-6 shadow-sm hover:shadow-md transition-all duration-200">
            <div className="flex flex-col h-full justify-between">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50">
                  <Calendar className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <div className="text-2xl font-display font-bold text-slate-900 mb-1">
                  {formatStreak(currentStreak)}
                </div>
                <p className="text-sm font-body text-slate-600">
                  Current streak
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Vertical Stack */}
      <div className="md:hidden space-y-4">
        {/* North Star Aspiration Card */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-coral/10 via-coral/5 to-transparent border border-coral/20 p-6 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-transparent" />
          
          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm">
                <Target className="h-5 w-5 text-royal-blue" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-xs font-display font-medium text-royal-blue uppercase tracking-wider mb-2">
              First you aspire, then you soar...
            </p>
            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full p-3 text-base font-display font-bold text-slate-900 bg-white/90 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                  rows={3}
                  placeholder="Enter your coaching goal..."
                  disabled={isUpdatingAspiration}
                />
                {error && (
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                )}
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={handleEditSave}
                    disabled={isUpdatingAspiration}
                    className="flex items-center gap-2 px-3 py-1.5 bg-royal-blue text-white text-sm font-medium rounded-lg hover:bg-royal-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isUpdatingAspiration ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Save
                  </button>
                  <button
                    onClick={handleEditCancel}
                    disabled={isUpdatingAspiration}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="group">
                <h2 className="font-display text-lg font-bold text-slate-900 tracking-tight leading-tight">
                  {userAspiration}
                </h2>
                {onAspirationUpdate && (
                  <button
                    onClick={handleEditStart}
                    className="mt-2 opacity-0 group-hover:opacity-100 p-1.5 text-slate-500 hover:text-royal-blue hover:bg-white/50 rounded-lg transition-all duration-200"
                    title="Edit your goal"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Row - Horizontal Scroll */}
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex-shrink-0 w-40 rounded-2xl bg-white border border-alto-grey p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <TrendingUp className="h-4 w-4 text-royal-blue" strokeWidth={1.5} />
              </div>
              <div className="text-lg font-display font-bold text-slate-900">
                {totalSessions}
              </div>
            </div>
            <p className="text-xs font-body text-slate-600">
              Sessions completed
            </p>
          </div>

          <div className="flex-shrink-0 w-40 rounded-2xl bg-white border border-alto-grey p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50">
                <Calendar className="h-4 w-4 text-green-600" strokeWidth={1.5} />
              </div>
              <div className="text-lg font-display font-bold text-slate-900">
                {formatStreak(currentStreak)}
              </div>
            </div>
            <p className="text-xs font-body text-slate-600">
              Current streak
            </p>
          </div>

          <div className="flex-shrink-0 w-40 rounded-2xl bg-white border border-alto-grey p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50">
                <Clock className="h-4 w-4 text-purple-600" strokeWidth={1.5} />
              </div>
              <div className="text-lg font-display font-bold text-slate-900">
                {minutesCoached}m
              </div>
            </div>
            <p className="text-xs font-body text-slate-600">
              Minutes coached
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotivationStatsHeader;
